import { useState, useRef, useEffect, useMemo } from "react";
import Fuse from "fuse.js";

// ─── FAQ Data ────────────────────────────────────────────────
interface FAQEntry {
  question: string;
  answer: string;
  keywords: string[];
  category: string;
}

const FAQ_DATA: FAQEntry[] = [
  // ── Getting Started ──
  {
    category: "Getting Started",
    question: "What is TradeCraft?",
    keywords: ["tradecraft", "what is", "platform", "about", "tell me", "explain"],
    answer:
      "TradeCraft is an algorithmic trading platform that lets anyone — from beginners to experts — automate their investing. You browse or build strategies (sets of trading rules), backtest them against historical data, and then invest real or simulated money through your own brokerage account. We support Alpaca (primary execution) and Charles Schwab (via Mirror Trading). TradeCraft handles the automation; your money stays in your own account. No coding required.",
  },
  {
    category: "Getting Started",
    question: "What is a strategy?",
    keywords: ["strategy", "what is", "definition", "explain", "mean", "trading strategy"],
    answer:
      "A strategy is a set of rules that tells TradeCraft when to buy and sell investments automatically. For example, a strategy might say 'rotate between stocks and bonds based on market momentum' or 'buy the dip when the S&P 500 drops 5%.' You can pick from our 10 community strategies (TC-01 through TC-10) or build your own with the Visual Strategy Builder.",
  },
  {
    category: "Getting Started",
    question: "How does TradeCraft work?",
    keywords: ["how", "work", "works", "process", "steps", "get started"],
    answer:
      "It's simple: (1) Browse or build a strategy, (2) Backtest it to see historical performance, (3) Connect your brokerage — Alpaca for execution, and optionally Charles Schwab for Mirror Trading, (4) Invest — TradeCraft automatically trades for you every day at 3:40 PM EST. Start with a free paper (simulated) account to practice risk-free!",
  },
  {
    category: "Getting Started",
    question: "What is algo trading?",
    keywords: ["algorithmic", "automated", "algorithm", "what is", "algo"],
    answer:
      "Algorithmic (algo) trading uses pre-defined rules and logic to automatically buy and sell investments. Instead of manually deciding when to trade, you write a strategy — a set of conditions like 'if the market drops 5%, buy more tech stocks' — and the system executes it for you. TradeCraft makes this accessible to everyone, no coding experience required.",
  },
  {
    category: "Getting Started",
    question: "I have never done algo trading before, can I do this?",
    keywords: ["beginner", "new", "first time", "never", "experience"],
    answer:
      "Absolutely! TradeCraft is designed for people with zero algo trading experience. You can start by browsing our community strategies — these are ready-made strategies you can backtest and invest in with one click. There's no coding required. We also offer a Visual Strategy Builder if you want to create your own. Start with a paper (simulated) account to practice risk-free.",
  },
  {
    category: "Getting Started",
    question: "Do I need experience with algo trading before I use this platform?",
    keywords: ["experience", "prerequisite", "skill", "knowledge"],
    answer:
      "No. TradeCraft is built for all levels. You can start by exploring community strategies, running backtests to see how they performed historically, and investing through a paper account to get comfortable — all without any prior algo trading experience.",
  },
  {
    category: "Getting Started",
    question: "Can I build my own strategies?",
    keywords: ["build", "create", "custom", "own", "strategy builder"],
    answer:
      "Yes! TradeCraft has a Visual Strategy Builder where you can drag-and-drop conditions, indicators, and assets to create your own strategy — no coding needed. You can also write strategies directly using our simple TradeCraft DSL syntax. Once built, backtest it instantly to see how it would have performed.",
  },

  // ── Trading ──
  {
    category: "Trading",
    question: "Do I need to actively trade?",
    keywords: ["active", "manually", "hands-on", "manual trading"],
    answer:
      "No. Once you invest in a strategy on TradeCraft, the system trades automatically based on the strategy's rules. You set it up once — choose a strategy, deposit funds, pick your rebalance settings — and TradeCraft handles the rest. Think of it as putting your portfolio on autopilot.",
  },
  {
    category: "Trading",
    question: "Do I need to actively monitor my trades?",
    keywords: ["monitor", "watch", "check", "keep eye"],
    answer:
      "No, you don't need to watch it constantly. TradeCraft manages the trading automatically. That said, it's always wise to periodically check on your portfolio — just like you'd glance at your bank account from time to time. We show your holdings, returns, and trade history right in the app.",
  },
  {
    category: "Trading",
    question: "What time does it automatically trade?",
    keywords: ["time", "when", "execute", "market close", "3:40", "schedule"],
    answer:
      "Automated trades execute at approximately 3:40 PM EST, about 20 minutes before market close. This timing aligns with historical backtesting (which uses end-of-day closing prices) and ensures your trades get filled before the market closes at 4:00 PM EST.",
  },
  {
    category: "Trading",
    question: "What time the trade executes?",
    keywords: ["time", "when", "execute", "schedule", "clock"],
    answer:
      "All automated trades execute at approximately 3:40 PM EST each trading day (about 20 minutes before market close). This timing is chosen to align with backtesting assumptions and ensure trades fill before close.",
  },
  {
    category: "Trading",
    question: "Does TradeCraft trade on my behalf?",
    keywords: ["trade", "behalf", "execute", "who trades"],
    answer:
      "TradeCraft sends trade orders to your connected Alpaca brokerage account based on the strategy rules you've chosen. The trades go directly to your personal Alpaca account. If you have Mirror Trading enabled, the same trades are automatically replicated to your Charles Schwab account. TradeCraft is the automation tool — Alpaca and Schwab are where the actual buying and selling happens. You maintain full control and ownership of your accounts at all times.",
  },
  {
    category: "Trading",
    question: "Do I have to manually build the strategy first?",
    keywords: ["manually", "build first", "required", "before trading"],
    answer:
      "Not at all. You can browse our library of community strategies, pick one you like, backtest it, and start investing immediately. If you want to create a custom strategy, you can use the Visual Strategy Builder — but it's completely optional.",
  },

  // ── Brokerage & Money ──
  {
    category: "Brokerage & Money",
    question: "Do I deposit my money with TradeCraft?",
    keywords: ["deposit", "money", "funds", "give money", "pay", "payment"],
    answer:
      "No. TradeCraft never holds your money. Your funds are deposited directly into your personal brokerage accounts (Alpaca and/or Charles Schwab), which you own and control. Your money sits in your own accounts, and TradeCraft simply tells them what to buy and sell based on your chosen strategy.",
  },
  {
    category: "Brokerage & Money",
    question: "Where is my money?",
    keywords: ["where", "money", "held", "stored", "location", "funds"],
    answer:
      "Your money is in your personal brokerage accounts at all times — Alpaca for strategy execution, and Charles Schwab if you use Mirror Trading. TradeCraft never holds, touches, or has access to withdraw your funds. Both Alpaca and Schwab are regulated brokerages — your accounts are protected by SIPC insurance.",
  },
  {
    category: "Brokerage & Money",
    question: "Does TradeCraft hold my stocks or cash?",
    keywords: ["hold", "stocks", "cash", "custody"],
    answer:
      "No. All your stocks and cash are held in your personal brokerage accounts — Alpaca and/or Charles Schwab. TradeCraft is simply the tool that automates the trading decisions. You have full access to your brokerage accounts at any time.",
  },
  {
    category: "Brokerage & Money",
    question: "Why is an Alpaca brokerage account needed?",
    keywords: ["alpaca", "why", "brokerage", "broker", "needed", "required"],
    answer:
      "TradeCraft is a strategy automation platform, not a brokerage. To actually buy and sell stocks, you need a brokerage account. We partner with Alpaca because they offer commission-free trading, a great API for automation, and support both paper (simulated) and live trading. Signing up is free and takes just a few minutes. You can also connect Charles Schwab to mirror all trades from Alpaca to your Schwab account automatically.",
  },
  {
    category: "Brokerage & Money",
    question: "Can I withdraw and liquidate a strategy?",
    keywords: ["withdraw", "liquidate", "cash out", "exit", "sell"],
    answer:
      "Yes. You can exit any strategy at any time. When you stop a strategy, TradeCraft will liquidate the holdings in that strategy within your Alpaca account (and your Schwab account if Mirror Trading is enabled), converting them back to cash. You can then withdraw directly from your brokerage accounts.",
  },
  {
    category: "Brokerage & Money",
    question: "Can I exit out of the strategy and cash out?",
    keywords: ["exit", "cash out", "stop", "leave"],
    answer:
      "Yes. You can stop any strategy at any time from your portfolio page. When you stop it, the system will sell the holdings and return the cash to your Alpaca account (and Schwab if mirroring). You can then withdraw from your brokerage accounts whenever you want.",
  },
  {
    category: "Brokerage & Money",
    question: "Can I test strategies in a paper account until I am comfortable?",
    keywords: ["paper", "test", "simulated", "practice", "comfortable"],
    answer:
      "Yes! This is exactly what we recommend. Alpaca offers a free paper trading account with simulated money. You can invest in any strategy using your paper account first, watch it trade for days or weeks, and only switch to live trading when you're confident and comfortable. Mirror Trading to Schwab only activates on your live account.",
  },

  // ── Risk & Performance ──
  {
    category: "Risk & Performance",
    question: "What is the risk?",
    keywords: ["risk", "risky", "dangerous", "lose money", "safe", "risks"],
    answer:
      "All investing carries risk, including the possibility of losing some or all of your investment. While TradeCraft's strategies use rules and risk management, no strategy is guaranteed to make money. Past backtested performance does not guarantee future results. We always recommend starting with a paper account and only investing money you can afford to lose.",
  },
  {
    category: "Risk & Performance",
    question: "Are backtests always guaranteed?",
    keywords: ["guarantee", "backtest", "accurate", "reliable", "trust"],
    answer:
      "No. Backtests show how a strategy would have performed in the past using historical data. They do not guarantee future results. Real-world trading differs due to slippage, timing differences, and changing market conditions. Use backtests as a research tool, not a crystal ball. A strategy with great backtested returns can still lose money in live markets.",
  },
  {
    category: "Risk & Performance",
    question: "What strategy statistics should I look at? What is CAGR? What is Sharpe Ratio?",
    keywords: ["statistics", "stats", "CAGR", "sharpe", "metrics", "look at"],
    answer:
      "Key statistics to evaluate:\n\n• CAGR (Compound Annual Growth Rate) — The average yearly return if gains were reinvested. A 50% CAGR means the strategy averaged 50% growth per year.\n\n• Sharpe Ratio — Measures return relative to risk. Higher is better. Above 1.0 is good, above 2.0 is excellent, above 3.0 is exceptional.\n\n• Max Drawdown — The largest peak-to-trough drop. A -20% drawdown means at its worst, the portfolio lost 20% from its high point.\n\n• Total Return — The overall percentage gain over the entire backtest period.\n\nLook for strategies with a good balance: high Sharpe ratio (risk-adjusted returns), manageable max drawdown (you can stomach the dips), and consistent CAGR.",
  },
  {
    category: "Risk & Performance",
    question: "What strategies might I want to try for moderate, conservative, and aggressive risk?",
    keywords: ["moderate", "conservative", "aggressive", "which strategy", "recommend", "try"],
    answer:
      "Here are some suggestions based on risk level:\n\n🟢 Conservative: TC-05 (Precision Alpha) and TC-06 (Leveraged Tri-Asset Rotation) — focus on risk management and steady returns.\n\n🟡 Moderate: TC-01 (All Weather Macro Momentum), TC-02 (Adaptive VIX Scale-In), TC-03 (Multi-Timeframe Trend Rotation), TC-08 (Tactical Defensive Allocation), TC-09 (Policy Hedge Diversifier) — balanced risk-reward.\n\n🔴 Aggressive: TC-07 (Dual Regime Hedge) and TC-10 (Leveraged Dip Capture) — higher potential returns with bigger drawdowns.\n\nAlways backtest and paper trade first before going live!",
  },

  // ── About TradeCraft ──
  {
    category: "About TradeCraft",
    question: "Who developed these strategies?",
    keywords: ["who", "developed", "created", "built", "author"],
    answer:
      "The community strategies (TC-01 through TC-10) were developed by the TradeCraft team and early users. They are shared publicly so anyone can backtest and evaluate them. However, TradeCraft does NOT recommend or endorse any strategy — they are research tools, not financial advice. You should always do your own due diligence.",
  },
  {
    category: "About TradeCraft",
    question: "How is it different from an ETF?",
    keywords: ["ETF", "difference", "fund", "index"],
    answer:
      "An ETF is a pre-built basket of stocks managed by a fund company. With TradeCraft, you own the individual stocks directly in your own brokerage account — not shares of a fund. Your strategy can change its holdings daily based on market conditions, while an ETF typically follows a fixed index. You have full transparency and control, and there are no fund management fees.",
  },
  {
    category: "About TradeCraft",
    question: "How is it different from Asset Management?",
    keywords: ["asset management", "wealth", "manager", "advisor"],
    answer:
      "Traditional asset managers charge 1-2% annually to manage your money, and you often don't know exactly what they're doing. With TradeCraft, you control the strategy, see every trade, own every stock directly, and pay a flat subscription fee — not a percentage of your portfolio. Your money never leaves your own account.",
  },
  {
    category: "About TradeCraft",
    question: "How is it different from a hedge fund?",
    keywords: ["hedge fund", "hedge", "fund"],
    answer:
      "Hedge funds require large minimum investments ($100K+), lock up your money for extended periods, charge 2% management + 20% of profits, and operate as a black box. TradeCraft lets you start with any amount, withdraw anytime, see exactly what's being traded, and pay a simple flat fee. Your money stays in your personal brokerage accounts (Alpaca and/or Schwab).",
  },
  {
    category: "About TradeCraft",
    question: "How is this different from simply trading stocks or ETFs?",
    keywords: ["different", "stocks", "manual", "buying stocks"],
    answer:
      "When you trade stocks manually, emotions and timing often hurt returns. TradeCraft automates the process — your strategy runs on predefined rules without emotional interference. It rebalances automatically, can monitor dozens of indicators simultaneously, and executes precisely at the right time every day. It's like having a tireless trading assistant that follows your rules exactly.",
  },
  {
    category: "About TradeCraft",
    question: "What if everyone trades the same strategy?",
    keywords: ["everyone", "same strategy", "crowded", "impact"],
    answer:
      "Great question! Because TradeCraft strategies trade highly liquid ETFs and large-cap stocks (like SPY, QQQ, TQQQ), even if thousands of users ran the same strategy, the trading volume would be a tiny fraction of these assets' daily volume (billions of dollars). There's no meaningful market impact. Each user trades in their own brokerage accounts (Alpaca and/or Schwab) independently.",
  },

  // ── Community Strategies ──
  {
    category: "Community Strategies",
    question: "What community strategies exist?",
    keywords: ["community", "list", "all strategies", "available", "strategies", "which"],
    answer:
      "TradeCraft currently offers 10 community strategies:\n\n• TC-01: All Weather Macro Momentum\n• TC-02: Adaptive VIX Scale-In\n• TC-03: Multi-Timeframe Trend Rotation\n• TC-04: Mean Reversion Momentum\n• TC-05: Precision Alpha\n• TC-06: Leveraged Tri-Asset Rotation\n• TC-07: Dual Regime Hedge\n• TC-08: Tactical Defensive Allocation\n• TC-09: Policy Hedge Diversifier\n• TC-10: Leveraged Dip Capture\n\nBrowse them all on the Community Strategies page to see their backtested performance.",
  },
  {
    category: "Community Strategies",
    question: "What type of annual returns are there in community strategies?",
    keywords: ["returns", "annual", "performance", "CAGR", "how much"],
    answer:
      "Backtested CAGR (Compound Annual Growth Rate) across community strategies ranges from roughly 30% to 80%+ depending on the strategy and the backtest period. For example, some aggressive strategies show 70-80% CAGR while conservative ones show 30-50%.\n\nRemember: these are backtested (historical) returns and do NOT guarantee future performance. Real trading results will differ.",
  },

  // ── Costs & Billing ──
  {
    category: "Costs & Billing",
    question: "Is there a referral code?",
    keywords: ["referral", "code", "discount", "promo"],
    answer:
      "Referral codes may be available from time to time. Check with the TradeCraft team or community for current offers. If you have a referral code, you can apply it on the Subscription page.",
  },
  {
    category: "Costs & Billing",
    question: "Is there a recurring monthly cost?",
    keywords: ["monthly", "recurring", "subscription", "cost", "price", "pricing", "how much"],
    answer:
      "TradeCraft offers both monthly and annual subscription plans. The exact pricing is shown on the Subscription page. There is a free tier that allows you to explore strategies and run backtests. Paid plans unlock features like automated trading and multiple strategy investments.",
  },
  {
    category: "Costs & Billing",
    question: "Does TradeCraft take from my profits?",
    keywords: ["profits", "commission", "fee", "fees", "percentage", "cut"],
    answer:
      "No. TradeCraft charges a flat subscription fee — we never take a percentage of your profits, gains, or portfolio value. What you earn is 100% yours. This is one of the key advantages over traditional asset management (which charges 1-2% of your portfolio) or hedge funds (which take 20% of profits).",
  },
  {
    category: "Costs & Billing",
    question: "What is the one time yearly cost?",
    keywords: ["yearly", "annual", "one time", "cost"],
    answer:
      "The annual plan offers a discounted rate compared to monthly billing. Check the Subscription page for the latest pricing. The annual plan is a one-time yearly payment with no hidden fees.",
  },
  {
    category: "Costs & Billing",
    question: "Where do I get my tax return from?",
    keywords: ["tax", "taxes", "return", "1099", "tax documents", "tax return"],
    answer:
      "Tax documents (like 1099 forms) come directly from your brokerages — Alpaca and Charles Schwab (if you use Mirror Trading). Since all your stocks and trades are in your personal brokerage accounts, they handle all tax reporting. You'll receive your tax documents during tax season, just like you would from any brokerage. TradeCraft does not issue tax documents.",
  },

  // ── Account & Security ──
  {
    category: "Account & Security",
    question: "Can I enable MFA?",
    keywords: ["MFA", "2FA", "two factor", "multi factor", "security", "secure", "authentication"],
    answer:
      "Yes! TradeCraft supports Multi-Factor Authentication (MFA) for your account. You can enable it from your Profile page. We highly recommend enabling MFA to add an extra layer of security to your account.",
  },
  {
    category: "Account & Security",
    question: "Do I get emails for the trades placed?",
    keywords: ["email", "notification", "trade alert", "confirmation"],
    answer:
      "Yes! Alpaca sends you email confirmations for every trade executed in your account. You'll get notified each time a buy or sell order is placed. You can also see all your trade history directly in the TradeCraft app and in your Alpaca dashboard.",
  },
  {
    category: "Account & Security",
    question: "Do I get to see what has been traded in my Alpaca?",
    keywords: ["see", "traded", "history", "orders", "transparency"],
    answer:
      "Yes. You can see all trades both in TradeCraft (under your portfolio and execution reports) and directly in your Alpaca account dashboard. Full transparency — nothing is hidden. Every order, every holding, every dollar is visible to you.",
  },

  // ── Platform Features ──
  {
    category: "Platform Features",
    question: "What is the Portfolio Analyzer?",
    keywords: ["portfolio analyzer", "analyzer", "correlation", "monte carlo", "sharpe", "optimization", "optimize"],
    answer:
      "The Portfolio Analyzer is a quantitative tool that helps you evaluate multi-strategy portfolios. It computes a full correlation matrix across your selected strategies, runs Sharpe ratio optimization for weight allocation, and performs Monte Carlo stress testing with 5,000 simulated paths. Three optimization modes: Max Sharpe (risk-adjusted), Max CAGR (highest growth), and Min Drawdown (lowest loss). It all runs in your browser — no server needed, results in under 100ms. Watch the walkthrough at /walkthroughs/PSPH4wTB21A",
  },
  {
    category: "Platform Features",
    question: "What is the Diversifier?",
    keywords: ["diversifier", "diversify", "combine strategies", "multi-strategy", "portfolio model", "weight"],
    answer:
      "The Diversifier lets you combine multiple strategies into one portfolio, assign custom weight allocations, and instantly see projected performance across best case, average, and worst case scenarios — all benchmarked against the S&P 500. It shows how diversification lowers drawdowns and smooths returns. Once you're happy with the model, you can go from projection to live execution in minutes. Watch the walkthrough at /walkthroughs/3WBjn27YM6Y",
  },
  {
    category: "Platform Features",
    question: "What is TradeCraft Pulse?",
    keywords: ["pulse", "market dashboard", "dashboard", "indices", "heatmap", "news", "sentiment", "fear and greed"],
    answer:
      "TradeCraft Pulse is a live market intelligence dashboard that puts everything you need in one auto-refreshing view: major indices, futures, and global markets; interactive charts for any symbol; sector heatmap; Fear & Greed Index and VIX; top gainers, losers, and most active; real-time financial news; treasury yields, commodities, and currencies; crypto prices; social sentiment from StockTwits and Reddit; and an earnings calendar. Market data refreshes every 30 seconds, news every 2 minutes. Watch the walkthrough at /walkthroughs/90dJ2Wk8RMU",
  },
  {
    category: "Platform Features",
    question: "What is Mirror Trading?",
    keywords: ["mirror", "mirror trading", "schwab", "charles schwab", "replicate", "copy trades"],
    answer:
      "Mirror Trading automatically replicates every trade from your Alpaca account to your Charles Schwab account in real time. When TradeCraft executes a buy or sell on Alpaca, the same trade is instantly mirrored to Schwab — same ticker, same quantity, same direction. Enable it with a single toggle after connecting your Schwab API credentials. This lets you run strategies on Alpaca (best API for automation) while keeping your primary portfolio at Schwab. Watch the walkthrough at /walkthroughs/1pTTITg39-k",
  },
  {
    category: "Platform Features",
    question: "Which brokerages are supported?",
    keywords: ["brokerage", "broker", "supported", "schwab", "alpaca", "which broker"],
    answer:
      "TradeCraft supports two brokerages:\n\n• Alpaca — Primary execution broker. Supports paper trading (simulated) and live trading. All strategy automation runs here. Commission-free.\n\n• Charles Schwab — Via Mirror Trading. Every trade executed on Alpaca is automatically replicated to your Schwab account in real time — same ticker, same quantity, same direction. Enable with one toggle.",
  },
  {
    category: "Platform Features",
    question: "What are execution logs?",
    keywords: ["execution logs", "logs", "trade log", "what traded", "why traded", "signal"],
    answer:
      "Execution logs show you exactly what was traded each day and why. Every buy, every sell, and the specific signal that triggered it. You get full transparency into the automated decisions — no black box. View them in your portfolio dashboard.",
  },
  {
    category: "Platform Features",
    question: "Can I override the optimizer's allocations?",
    keywords: ["override", "custom allocation", "adjust weights", "manual allocation"],
    answer:
      "Yes. The Portfolio Analyzer calculates optimal weights, but you can override them with your own dollar amounts. The equity curve and Monte Carlo simulation recalculate instantly with your custom allocations. A one-click reset returns to the optimizer's suggestion. Watch the walkthrough at /walkthroughs/XkaFtGM1BPY",
  },
  {
    category: "Platform Features",
    question: "Can I run multiple strategies at the same time?",
    keywords: ["multiple strategies", "more than one", "several strategies", "simultaneous"],
    answer:
      "Yes! You can invest in multiple strategies simultaneously. The Diversifier and Portfolio Analyzer are specifically designed to help you evaluate and manage multi-strategy portfolios — showing how combining strategies improves risk-adjusted returns through diversification.",
  },
  {
    category: "Platform Features",
    question: "What is Monte Carlo simulation?",
    keywords: ["monte carlo", "simulation", "simulated paths", "stress test"],
    answer:
      "Monte Carlo simulation generates thousands of randomized future paths based on a strategy's historical characteristics (return, volatility, drawdown patterns). It helps you understand the range of possible outcomes — not just the backtested path — by showing best case, worst case, and probability distributions. TradeCraft runs 5,000 simulated paths in the Portfolio Analyzer and Diversifier to stress-test your allocations.",
  },
];

const CATEGORIES = [
  "Getting Started",
  "Trading",
  "Brokerage & Money",
  "Platform Features",
  "Risk & Performance",
  "About TradeCraft",
  "Community Strategies",
  "Costs & Billing",
  "Account & Security",
];

// ─── Chat Types ──────────────────────────────────────────────
interface ChatMessage {
  id: number;
  type: "bot" | "user";
  text: string;
  isChips?: boolean;
  chips?: string[];
}

const GREETING =
  "Hey there! 👋 I'm the TradeCraft assistant. I can help you learn about the platform, strategies, trading, costs, and more. Pick a topic below or type your question!";

// ─── Component ───────────────────────────────────────────────
export function HelpChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const nextId = useRef(1);

  const fuse = useMemo(
    () =>
      new Fuse(FAQ_DATA, {
        keys: [
          { name: "question", weight: 0.5 },
          { name: "keywords", weight: 0.3 },
          { name: "answer", weight: 0.2 },
        ],
        threshold: 0.55,
        includeScore: true,
      }),
    []
  );

  // Initialize with greeting
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([
        { id: nextId.current++, type: "bot", text: GREETING },
      ]);
    }
  }, [messages.length]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  function addBotMessage(text: string) {
    setMessages((prev) => [
      ...prev,
      { id: nextId.current++, type: "bot", text },
    ]);
  }

  function findByKeywords(text: string): FAQEntry | null {
    const lower = text.toLowerCase();
    // Strip filler words to get intent tokens
    const stripped = lower
      .replace(/\b(tell|me|about|what|is|the|a|an|how|do|does|i|can|my|where|why|when|are|there|it|this|that|please|help|explain|know|want|to|get|from|for|of|in|on|with|and|or)\b/g, "")
      .replace(/[?!.,]/g, "")
      .trim();
    const tokens = stripped.split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return null;

    let best: FAQEntry | null = null;
    let bestScore = 0;

    for (const entry of FAQ_DATA) {
      let score = 0;
      const kwLower = entry.keywords.map((k) => k.toLowerCase());
      const qLower = entry.question.toLowerCase();

      for (const token of tokens) {
        // Check if token matches any keyword (substring match)
        for (const kw of kwLower) {
          if (kw.includes(token) || token.includes(kw)) {
            score += 3;
            break;
          }
        }
        // Check if token appears in the question
        if (qLower.includes(token)) {
          score += 2;
        }
      }
      if (score > bestScore) {
        bestScore = score;
        best = entry;
      }
    }
    // Require at least one meaningful keyword hit
    return bestScore >= 3 ? best : null;
  }

  function handleUserQuestion(text: string) {
    // Add user message
    setMessages((prev) => [
      ...prev,
      { id: nextId.current++, type: "user", text },
    ]);

    // 1) Try keyword matching first (handles natural language like "tell me about taxes")
    const keywordMatch = findByKeywords(text);
    if (keywordMatch) {
      addBotMessage(keywordMatch.answer);
    } else {
      // 2) Fall back to Fuse.js fuzzy matching
      const results = fuse.search(text);
      if (results.length > 0 && results[0].score !== undefined && results[0].score < 0.6) {
        addBotMessage(results[0].item.answer);
      } else {
        addBotMessage(
          "I don't have an answer for that yet. Please reach out to us at info@colvix.net and we'll be happy to help! 📧"
        );
      }
    }
    setActiveCategory(null);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) return;
    setInput("");
    handleUserQuestion(trimmed);
  }

  function handleChipClick(question: string) {
    handleUserQuestion(question);
  }

  function handleCategoryClick(category: string) {
    setActiveCategory(category === activeCategory ? null : category);
  }

  function handleReset() {
    nextId.current = 1;
    setMessages([{ id: nextId.current++, type: "bot", text: GREETING }]);
    setActiveCategory(null);
    setInput("");
  }

  const categoryQuestions = activeCategory
    ? FAQ_DATA.filter((f) => f.category === activeCategory)
    : [];

  return (
    <>
      {/* Floating button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 text-white shadow-lg shadow-primary-500/30 hover:shadow-primary-500/50 hover:scale-105 transition-all flex items-center justify-center"
          aria-label="Open help chat"
        >
          <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div className="fixed bottom-5 right-5 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100dvh-6rem)] flex flex-col rounded-2xl border border-surface-700/60 bg-surface-950/95 backdrop-blur-xl shadow-2xl shadow-black/40 overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-surface-800/60 bg-surface-900/60">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-surface-100">TradeCraft Help</p>
              <p className="text-xs text-accent-400">Ask me anything</p>
            </div>
            <button
              onClick={handleReset}
              className="p-1.5 rounded-lg hover:bg-surface-800/60 text-surface-400 hover:text-surface-200 transition-colors"
              title="Clear chat"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 rounded-lg hover:bg-surface-800/60 text-surface-400 hover:text-surface-200 transition-colors"
              title="Close chat"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-line ${
                    msg.type === "user"
                      ? "bg-primary-500/20 border border-primary-500/30 text-surface-100 rounded-br-md"
                      : "bg-surface-800/60 border border-surface-700/40 text-surface-200 rounded-bl-md"
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Category chips + question chips */}
          <div className="border-t border-surface-800/40">
            {/* Category pills */}
            <div className="px-3 pt-2 pb-1 flex flex-wrap gap-1.5">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => handleCategoryClick(cat)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                    activeCategory === cat
                      ? "bg-primary-500/20 border-primary-500/40 text-primary-300"
                      : "bg-surface-800/40 border-surface-700/40 text-surface-400 hover:text-surface-200 hover:border-surface-600/60"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Question chips for selected category */}
            {activeCategory && categoryQuestions.length > 0 && (
              <div className="px-3 pb-2 max-h-32 overflow-y-auto custom-scrollbar">
                <div className="flex flex-col gap-1">
                  {categoryQuestions.map((faq, i) => (
                    <button
                      key={i}
                      onClick={() => handleChipClick(faq.question)}
                      className="text-left text-xs px-3 py-2 rounded-lg bg-surface-800/30 border border-surface-700/30 text-surface-300 hover:text-surface-100 hover:bg-surface-800/60 hover:border-primary-500/30 transition-colors"
                    >
                      {faq.question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <form onSubmit={handleSubmit} className="flex items-center gap-2 px-3 pb-3 pt-1">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your question..."
                className="flex-1 bg-surface-800/50 border border-surface-700/50 rounded-xl px-4 py-2.5 text-sm text-surface-100 placeholder-surface-500 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/20"
              />
              <button
                type="submit"
                disabled={!input.trim()}
                className="p-2.5 rounded-xl bg-primary-500 hover:bg-primary-400 disabled:opacity-30 disabled:cursor-not-allowed text-white transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7l7 7" />
                </svg>
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
